import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Alert, Button, Grid, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { LocalizationContext } from '@cvt/contexts';
import { emailRegex } from '@cvt/helpers/validation';
import { useRouter } from '@cvt/routing';

import { AuthContext } from '../contexts/AuthContext';
import { LoggedOutActionsLayout } from '../components/LoggedOutActionsLayout';

interface Props {
  onResetPasswordEmailSent?: () => void;
  onBackToLogin?: () => void;
};

export const ResetPassword: React.FC<Props> = (props) => {
  const { onResetPasswordEmailSent, onBackToLogin } = props;
  const router = useRouter();
  const [success, setSuccess] = React.useState(false);
  const { dictionary } = React.useContext(LocalizationContext);
  const { resetPassword } = React.useContext(AuthContext);
  const { control, setError, handleSubmit, formState: { isSubmitting, errors } } = useForm({
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = React.useCallback(async (data: Auth.ResetPassword) => {
    try {
      resetPassword(data.email);
      setSuccess(true);
      onResetPasswordEmailSent?.();
    } catch(e: any) {
      // @ts-ignore
      setError('email', { type: 'error', message: dictionary.auth.firebase.errors[e.error] || e.message });
      setSuccess(false);
    }
  }, [resetPassword, onResetPasswordEmailSent, setError, dictionary]);

  return (
    <LoggedOutActionsLayout>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container direction="column" spacing={2}>
          <Grid size={12}>
            <Typography variant="h4" align="center">{dictionary.auth.resetPassword.title}</Typography>
          </Grid>
          {success && (
            <Grid size={12}>
              <Alert severity="success">{dictionary.auth.resetPassword.passwordResetEmailSent}</Alert>
            </Grid>
          )}
          <Grid size={12}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: dictionary.forms.validations.required,
                pattern: {
                  value: emailRegex,
                  message: dictionary.forms.validations.invalidEmail,
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="outlined"
                  label={dictionary.forms.fieldEmail}
                  name="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
          </Grid>
          <br/><br/>
          <Grid size={12}>
            <Grid container justifyContent="space-between" spacing={2}>
              <Grid>
                <Button
                  size="large"
                  type="button"
                  variant="contained"
                  color="secondary"
                  onClick={() => onBackToLogin ? onBackToLogin() : router.auth.login.go()}
                >
                  {dictionary.auth.resetPassword.buttonLogin}
                </Button>
              </Grid>
              {!success && (
                <>
                  <div style={{ marginLeft: 'auto' }}/>
                  <Grid>
                    <LoadingButton loading={isSubmitting} size="large" type="submit" variant="contained" color="primary">
                      {dictionary.auth.resetPassword.buttonReset}
                    </LoadingButton>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </form>
    </LoggedOutActionsLayout>
  );
};
